/**
 * سربرگِ «بیرون از پاکت» (WHOSUP-286) — رویدادهایی که منبعِ یک پاکتِ شهر
 * پیدا کرده ولی شهرشان بیرون از آن پاکت است، و شهرِ خودشان هم نداشتشان.
 *
 * فقط‌خواندنی: هیچ حکمی از این‌جا به کرالر برنمی‌گردد. مواردی که شهرِ خودِ
 * رویداد همان را داشت در کرالر حذف شده‌اند و این‌جا نمی‌آیند.
 *
 * تاریخ به وقتِ شهرِ رویداد است، نه مرورگر — منطق در `core/lib/outOfBag.ts`.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { opsRepo } from '@/core/data/opsRepo';
import { filterOutOfBag, formatOutOfBagStart, outOfBagSources } from '@/core/lib/outOfBag';
import { useUrlState } from '@/app/useUrlState';

export function OutOfBagTab() {
  const q = useQuery({ queryKey: ['outOfBagEvents'], queryFn: opsRepo.outOfBagEvents });
  const [bag, setBag] = useUrlState('bag', '');
  const [source, setSource] = useUrlState('source', '');

  if (q.isLoading) return <p className="dim">…</p>;
  if (q.error) return <p className="err">فهرست نیامد.</p>;
  const data = q.data;
  if (!data || data.error) {
    return <p className="err">کشِ گزارش‌های کرالر روی این دیتابیس نیست{data?.error ? ` (${data.error})` : ''}.</p>;
  }

  const rows = filterOutOfBag(data.events, bag || null, source || null);
  const sources = outOfBagSources(data.events, bag || null);

  return (
    <div className="card">
      <h2>رویدادهای دورریخته‌ی بیرون از پاکت</h2>
      <p className="dim" style={{ marginBottom: 10 }}>
        رویدادهایی که منبعِ یک پاکت پیدا کرده ولی شهرشان بیرون از آن پاکت است و منابعِ
        شهرِ خودشان همان را نداشتند — یعنی چیزی که از دست رفته. فهرستِ خالیِ یک پاکت
        یعنی «هیچ چیز دور ریخته نشد»، نه «حساب نشده».
      </p>

      {data.bags.length === 0 ? (
        <p className="dim">هنوز هیچ ردیفی از کرالر نرسیده — گزارش بعد از مایگریشنِ 0056ِ کرالر و اجرای شبانه می‌آید.</p>
      ) : (
        <div className="scrollx" style={{ marginBottom: 12 }}>
          <table className="tbl">
            <thead>
              <tr><th>پاکت</th><th>شهرها</th><th>رویداد</th><th>زمانِ محاسبه</th></tr>
            </thead>
            <tbody>
              {data.bags.map((b) => (
                <tr key={b.bag}>
                  <td className="mono" dir="ltr">{b.bag}</td>
                  <td className="dim mono" dir="ltr">{b.cities.join(', ') || '—'}</td>
                  <td className="mono">{b.count}</td>
                  <td className="dim mono" dir="ltr">
                    {b.computed_at ? b.computed_at.replace('T', ' ').slice(0, 16) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="filters" style={{ marginBottom: 10 }}>
        <select value={bag} onChange={(e) => { setBag(e.target.value); setSource(''); }}>
          <option value="">همه‌ی پاکت‌ها</option>
          {data.bags.map((b) => <option key={b.bag} value={b.bag}>{b.bag}</option>)}
        </select>
        <select value={source} onChange={(e) => setSource(e.target.value)}>
          <option value="">همه‌ی منبع‌ها</option>
          {sources.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <span className="spacer" />
        <span className="dim">{rows.length} رویداد</span>
      </div>

      <div className="scrollx">
        <table className="tbl">
          <thead>
            <tr>
              <th>پاکت</th>
              <th>منبع</th>
              <th>شهرِ منبع</th>
              <th>شهرِ رویداد</th>
              <th>عنوان</th>
              <th>تاریخ (وقتِ شهرِ رویداد)</th>
              <th>لینک</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.bag}|${r.url ?? ''}|${i}`}>
                <td className="mono" dir="ltr">{r.bag}</td>
                <td className="mono" dir="ltr">{r.source_id ?? '—'}</td>
                <td className="mono" dir="ltr">{r.source_city ?? '—'}</td>
                <td className="mono" dir="ltr">{r.event_city ?? '—'}</td>
                <td dir="auto" title={r.venue_address ?? undefined}>{r.title ?? '—'}</td>
                <td className="mono" dir="ltr" title={r.event_tz ?? 'منطقه‌ی شهر نامعلوم — ساعتِ خامِ منبع'}>
                  {formatOutOfBagStart(r.starts_at, r.event_tz)}
                </td>
                <td>
                  {r.url ? <a href={r.url} target="_blank" rel="noreferrer">باز کن</a> : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
